/**
 * EmptyState & ErrorState Components — HMVMReviewer Design System Phase 1
 * 
 * Features:
 * - EmptyState: Display when no data/content is available
 * - ErrorState: Display when an error occurs
 * - Icon, title, description, and action button support
 * - Uses semantic theme tokens
 */

import React, { HTMLAttributes } from 'react';
import { Button } from './Button';
import { useLocale } from '../../contexts/LocaleContext';

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className = '',
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={`
        flex flex-col items-center justify-center
        py-12 px-4
        text-center
        ${className}
      `}
      {...props}
    >
      {icon && (
        <div className="mb-4 text-[var(--reviewer-text-tertiary)]">
          {icon}
        </div>
      )}
      
      <h3 className="text-[var(--reviewer-text)] text-[var(--reviewer-text-xl)] font-medium font-mono mb-2">
        {title}
      </h3>
      
      {description && (
        <p className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-base)] font-mono max-w-md mb-6">
          {description}
        </p>
      )}
      
      {action && (
        <Button variant="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}

/**
 * Error State Component
 */
export interface ErrorStateProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  error?: Error | string;
  onRetry?: () => void;
  retryLabel?: string;
}

export function ErrorState({
  title,
  error,
  onRetry,
  retryLabel,
  className = '',
  ...props
}: ErrorStateProps) {
  const { t } = useLocale();
  const resolvedTitle = title ?? t('empty.generic');
  const resolvedRetry = retryLabel ?? t('empty.retry');
  const errorMessage = error
    ? typeof error === 'string'
      ? error
      : error.message
    : undefined;

  return (
    <div
      className={`
        flex flex-col items-center justify-center
        py-12 px-4
        text-center
        ${className}
      `}
      role="alert"
      {...props}
    >
      <div className="mb-4 text-[var(--reviewer-danger)]">
        <svg
          className="w-16 h-16"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="square"
            strokeLinejoin="miter"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      
      <h3 className="text-[var(--reviewer-text)] text-[var(--reviewer-text-xl)] font-medium font-mono mb-2">
        {resolvedTitle}
      </h3>
      
      {errorMessage && (
        <p className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-base)] font-mono max-w-md mb-2">
          {errorMessage}
        </p>
      )}
      
      {onRetry && (
        <div className="mt-6">
          <Button variant="primary" onClick={onRetry}>
            {resolvedRetry}
          </Button>
        </div>
      )}
    </div>
  );
}
